import axios from "axios";
import {  hasAuth } from "./auth.header";
import { ApiAnalysisDatasInfos, ApiCentralizerRace, ApiDeltaPoint, ApiFileInfos, ApiGpsPoint, ApiSimulatorConfig, ApiSimulatorState } from "../types";

/* -------------------------------------------------------------------------------- *\
|                               findapiserverurl
\* -------------------------------------------------------------------------------- */
export const findapiserverurl = (): string =>
{
	if (process.env.REACT_APP__API_URL) return process.env.REACT_APP__API_URL;

	return 'https://simulator.mclloyd.com';
}
/* -------------------------------------------------------------------------------- *\
|                               findwsserverurl
\* -------------------------------------------------------------------------------- */
export const findwsserverurl = (): string =>
{
	if (process.env.REACT_APP__WS_URL) return process.env.REACT_APP__WS_URL;

	return 'https://simulator.mclloyd.com/ws';
}
/* -------------------------------------------------------------------------------- *\
|                               get_sources
\* -------------------------------------------------------------------------------- */
export const get_sources = async () : Promise<string[]> =>
{
	if (!hasAuth())
	{
		return Promise.resolve([]);
	}
	return Promise.resolve()
		.then(() => axios.get(findapiserverurl() + '/api/getsources'))
		.then(response => 
		{
			var sources = response.data as string[];
			return sources;
		})
		.catch(error =>
		{
			if (error.response.data.message)
			{
				throw new Error(error.response.data.message);
			}
			else
			{
				throw error;
			}
		});	
}
/* -------------------------------------------------------------------------------- *\
|                               get_sourcefiles
\* -------------------------------------------------------------------------------- */
export const get_sourcefiles = async (source: string) : Promise<string[]> =>
{
	if (!hasAuth())
	{
		return Promise.resolve([]);
	}
	return Promise.resolve()
		.then(() => axios.get(findapiserverurl() + '/api/getsourcedatas', { params: { source: source }}))
		.then(response => 
		{
			var sources = response.data as string[];
			return sources;
		})
		.catch(error =>
		{
			if (error.response.data.message)
			{
				throw new Error(error.response.data.message);
			}
			else
			{
				throw error;
			}
		});
}
/* -------------------------------------------------------------------------------- *\
|                               post_doimport
\* -------------------------------------------------------------------------------- */
export const post_doimport = async (source: string, file: string, importname: string) : Promise<boolean> =>
{
	if (!hasAuth())
	{
		return Promise.resolve(false);
	}
	return Promise.resolve()
		.then(() => axios.get(findapiserverurl() + '/api/doimport', { params: { source: source, file: file, importname: importname }}))
		.then(() => 
		{
			return Promise.resolve(true);
		})
		.catch(error =>
		{
			if (error.response.data.message)
			{
				throw new Error(error.response.data.message);
			}
			else
			{
				throw error;
			}
		});
}
/* -------------------------------------------------------------------------------- *\
|                               get_checkimport
\* -------------------------------------------------------------------------------- */
export const get_checkimport = async () : Promise<string> =>
{
	if (!hasAuth())
	{
		return Promise.resolve("error");
	}
	return Promise.resolve()
		.then(() => axios.get(findapiserverurl() + '/api/checkimport'))
		.then((response) => 
		{
			return Promise.resolve(response.data.state);
		})
		.catch(error =>
		{
			if (error.response.data.message)
			{
				throw new Error(error.response.data.message);
			}
			else
			{
				throw error;
			}
		});
}
/* -------------------------------------------------------------------------------- *\
|                               get_datas
\* -------------------------------------------------------------------------------- */
export const get_datas = async () : Promise<string[]> =>
{
	if (!hasAuth())
	{
		return Promise.resolve([]);
	}
	return Promise.resolve()
		.then(() => axios.get(findapiserverurl() + '/api/getdatas'))
		.then(response => 
		{
			var sources = response.data as string[];
			return sources;
		})
		.catch(error =>
		{
			if (error.response.data.message)
			{
				throw new Error(error.response.data.message);
			}
			else
			{
				throw error;
			}
		});	
}
/* -------------------------------------------------------------------------------- *\
|                               get_state
\* -------------------------------------------------------------------------------- */
export const get_state = async () : Promise<ApiSimulatorState | null> =>
{
	if (!hasAuth())
	{
		return Promise.resolve(null);
	}
	return Promise.resolve()
		.then(() => axios.get(findapiserverurl() + '/api/getstate'))
		.then(response => 
		{
			var state = response.data as ApiSimulatorState;
			return state;
		})
		.catch(error =>
		{
			if (error.response.data.message)
			{
				throw new Error(error.response.data.message);
			}
			else
			{
				throw error;
			}
		});	
}
/* -------------------------------------------------------------------------------- *\
|                               post_config
\* -------------------------------------------------------------------------------- */
export const post_config = async (config: ApiSimulatorConfig) : Promise<boolean> =>
{
	if (!hasAuth())
	{
		return Promise.resolve(false);
	}
	return Promise.resolve()
		.then(() => axios.post(findapiserverurl() + '/api/setconfig', config))
		.then(response => 
		{
			return Promise.resolve(true);
		})
		.catch(error =>
		{
			if (error.response.data.message)
			{
				throw new Error(error.response.data.message);
			}
			else
			{
				throw error;
			}
		});	
}
/* -------------------------------------------------------------------------------- *\
|                               post_tracker_setstate
\* -------------------------------------------------------------------------------- */
const post_tracker_setstate = async (path: string): Promise<void> =>
{
	if (!hasAuth()) return;
	return Promise.resolve()
		.then(() => axios.post(findapiserverurl() + path))
		.then(() => { })
		.catch(error => { throw new Error(error.response?.data?.message ?? String(error)); });
}
export const post_tracker_stop    = (): Promise<void> => post_tracker_setstate('/api/trackers/stop');
export const post_tracker_start   = (): Promise<void> => post_tracker_setstate('/api/trackers/start');
export const post_tracker_startsim = (startAt: number): Promise<void> =>
{
	if (!hasAuth()) return Promise.resolve();
	return Promise.resolve()
		.then(() => axios.post(findapiserverurl() + '/api/trackers/startsim', { start: startAt }))
		.then(() => { })
		.catch(error => { throw new Error(error.response?.data?.message ?? String(error)); });
};
export const post_tracker_setactive = (id: number, active: boolean): Promise<void> =>
{
	if (!hasAuth()) return Promise.resolve();
	return Promise.resolve()
		.then(() => axios.post(findapiserverurl() + `/api/trackers/${id}/active`, { active }))
		.then(() => { })
		.catch(error => { throw new Error(error.response?.data?.message ?? String(error)); });
};
/* -------------------------------------------------------------------------------- *\
|                               get_analysis_datasinfos
\* -------------------------------------------------------------------------------- */
export const get_analysis_datasinfos = async (datas: string): Promise<ApiAnalysisDatasInfos | null> =>
{
	if (!hasAuth()) return Promise.resolve(null);
	return Promise.resolve()
		.then(() => axios.get(findapiserverurl() + '/api/analysis/datasinfos', { params: { datas } }))
		.then(response => response.data as ApiAnalysisDatasInfos)
		.catch(error => { throw new Error(error.response?.data?.message ?? String(error)); });
}
/* -------------------------------------------------------------------------------- *\
|                               get_analysis_trackergps
\* -------------------------------------------------------------------------------- */
export const get_analysis_trackergps = async (datas: string, tracker: number): Promise<ApiGpsPoint[]> =>
{
	if (!hasAuth()) return Promise.resolve([]);
	return Promise.resolve()
		.then(() => axios.get(findapiserverurl() + '/api/analysis/trackergps', { params: { datas, tracker } }))
		.then(response => response.data as ApiGpsPoint[])
		.catch(error => { throw new Error(error.response?.data?.message ?? String(error)); });
}
/* -------------------------------------------------------------------------------- *\
|                               get_analysis_fusiongps
\* -------------------------------------------------------------------------------- */
export const get_analysis_fusiongps = async (datas: string, fusion: number, type: 'raw' | 'fused'): Promise<ApiGpsPoint[]> =>
{
	if (!hasAuth()) return Promise.resolve([]);
	return Promise.resolve()
		.then(() => axios.get(findapiserverurl() + '/api/analysis/fusiongps', { params: { datas, fusion, type } }))
		.then(response => response.data as ApiGpsPoint[])
		.catch(error => { throw new Error(error.response?.data?.message ?? String(error)); });
}
/* -------------------------------------------------------------------------------- *\
|                               get_analysis_trackerdelta
\* -------------------------------------------------------------------------------- */
export const get_analysis_trackerdelta = async (datas: string, tracker: number): Promise<ApiDeltaPoint[]> =>
{
	if (!hasAuth()) return Promise.resolve([]);
	return Promise.resolve()
		.then(() => axios.get(findapiserverurl() + '/api/analysis/trackerdelta', { params: { datas, tracker } }))
		.then(response => response.data as ApiDeltaPoint[])
		.catch(error => { throw new Error(error.response?.data?.message ?? String(error)); });
}
/* -------------------------------------------------------------------------------- *\
|                               get_fileinfos
\* -------------------------------------------------------------------------------- */
export const get_fileinfos = async (source: string, file: string): Promise<ApiFileInfos | null> =>
{
	if (!hasAuth()) return Promise.resolve(null);
	return Promise.resolve()
		.then(() => axios.get(findapiserverurl() + '/api/getfileinfos', { params: { source, file } }))
		.then(response => response.data as ApiFileInfos)
		.catch(error =>
		{
			if (error.response?.data?.message) throw new Error(error.response.data.message);
			throw error;
		});
}
/* -------------------------------------------------------------------------------- *\
|                               post_doremove
\* -------------------------------------------------------------------------------- */
export const post_doremove = async (datas: string) : Promise<boolean> =>
{
	if (!hasAuth())
	{
		return Promise.resolve(false);
	}
	return Promise.resolve()
		.then(() => axios.get(findapiserverurl() + '/api/doremove', { params: { datas: datas }}))
		.then(() => 
		{
			return Promise.resolve(true);
		})
		.catch(error =>
		{
			if (error.response.data.message)
			{
				throw new Error(error.response.data.message);
			}
			else
			{
				throw error;
			}
		});
}
/* -------------------------------------------------------------------------------- *\
|                               get_file_races
\* -------------------------------------------------------------------------------- */
export const get_file_races = async (source: string, t_min: number, t_max: number): Promise<ApiCentralizerRace[]> =>
{
	if (!hasAuth()) return Promise.resolve([]);
	return Promise.resolve()
		.then(() => axios.get(findapiserverurl() + '/api/centralizer/fileraces', { params: { source, t_min, t_max } }))
		.then(response => response.data as ApiCentralizerRace[])
		.catch(error => { throw new Error(error.response?.data?.message ?? String(error)); });
}
